import { call, put, select, takeLatest } from 'redux-saga/effects'
import { setCadRecords } from '../../options/store/options.actions'
import { getTripInformationSelector } from '../../tripInformation/selectors'
import { ITripInformationState } from '../../tripInformation/store/tripInformation.types'
import { getServiceIdSelector, getUserSelector } from '../../user/selectors'
import { IUserState } from '../../user/store/user.types'
import { CadImportService } from '../services/cadImport.service'
import { setCadRecordsResponse, setUserCanDisableUnitFilter } from '../store/cadImport.actions'
import { CadImportConstants } from '../store/cadImport.constants'
import { CadImportRequest, CadImportResponse, ICadImportState } from '../store/cadImport.types'
import Logger from '../../../services/logger.service'
import { getCadImportSelector } from '../selectors'
import { handleApiError } from '../../../components/apiErrorHandler/store/apiErrorHandler.actions'
import { getAuthenticationJwtSelector } from '../../login/selectors/getAuthenticationJwt.selector'
import { setAuthenticationJwt } from '../../login/store/login.actions'
import { getConfigurationsSelector } from '../../configurations/selectors'
import { IConfigurationsState } from '../../configurations/store/configurations.types'

export function* getCadRecords() {
  try {
    const serviceId: string = yield select(getServiceIdSelector)
    const user: IUserState = yield select(getUserSelector)
    const { userId } = user
    const tripInformation: ITripInformationState = yield select(getTripInformationSelector)
    const { unit } = tripInformation
    const cadImport: ICadImportState = yield select(getCadImportSelector)
    const { disableUnitFilteringFlag } = cadImport
    const configurationsState: IConfigurationsState = yield select(getConfigurationsSelector)
    const { cadImportConfig } = configurationsState
    const authenticationJwt = yield select(getAuthenticationJwtSelector)
    const filterByUnit = !disableUnitFilteringFlag
    const serviceFiltersByUnit = cadImportConfig?.filter_unit === 1

    if (!serviceId) {
      throw Error('serviceId does not exist')
    }

    if (!userId) {
      throw Error('userId does not exist')
    }

    if (serviceFiltersByUnit && !unit && filterByUnit) {
      throw Error('Unit has not been set. Either set the unit or Disable Unit Filtering to search all units.')
    }

    const cadImportRequest: CadImportRequest = {
      serviceId: +serviceId,
      userId: +userId,
    }

    if (serviceFiltersByUnit && filterByUnit) {
      cadImportRequest.filters = [{ name: 'UnitId', value: +unit!.id! }]
    }

    const cadImportService = new CadImportService(authenticationJwt as string)

    const response: CadImportResponse = yield call([cadImportService, cadImportService.doCadImport], cadImportRequest)

    response.success = false
    response.failure = false

    Logger.info(`CadImportResponse status: ${response.status}`)

    if (response.status === 200) {
      yield put(setAuthenticationJwt(response.authenticationJwt))
      if (response.data) {
        yield put(setUserCanDisableUnitFilter(response.data.userCanDisableUnitFilter))
      }
      // Only check permission when unit filtering is enabled at service level (filter_unit === 1)
      // When filter_unit === 0, unit filtering is disabled at service level, so no permission check needed
      // The privilege check only makes sense when the service has unit filtering turned on (filter_unit === 1)
      // and the user wants to turn off the filter by unit toggle
      if (serviceFiltersByUnit && disableUnitFilteringFlag && !response.data?.userCanDisableUnitFilter) {
        response.failure = true
        response.responseMessage = `You don't have the required role or privilege to disable unit filtering.`
      } else if (response.data?.cadRecords.length) {
        response.success = true
        Logger.info(`CadImportResponse number of records: ${response.data.cadRecords.length}`)

        yield put(setCadRecords(response.data.cadRecords))
      } else if (response.data === null && response.messages?.length) {
        response.failure = true
        const index = response.messages.findIndex((x) => x.status === response.status)
        response.responseMessage = response.messages[index].message
      } else {
        response.failure = true
        response.responseMessage = 'There are currently no CAD records available.'
      }
    } else {
      response.failure = true
      if (response.messages?.length) {
        const index = response.messages.findIndex((x) => x.status === response.status)
        response.responseMessage = response.messages[index].message
      } else {
        response.responseMessage =
          'There was an error processing your CAD import. Please contact customer support for further assistance.'
      }
      yield put(handleApiError(response))
    }
    const subsetOfResponse = (({ success, failure, responseMessage }) => ({
      success,
      failure,
      responseMessage,
    }))(response)
    yield put(setCadRecordsResponse(subsetOfResponse))
  } catch (e: any) {
    let cause = ''
    if (e.messages && e.messages.length) {
      const index = e.messages.findIndex((x: any) => x.status === e.status)
      cause = e.messages[index].message
    } else if (e.message) {
      cause = e.message
    } else {
      cause = 'unknown'
    }
    const errorResponse = CadImportService.createErrorResponse(`An error occurred during CAD Import. Cause: ${cause}`)
    yield put(setCadRecordsResponse(errorResponse))
    yield put(handleApiError(e))
  }
}

export function* getCadRecordsSaga() {
  yield takeLatest([CadImportConstants.GET_CAD_RECORDS], getCadRecords)
}
